import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import type { RecoverStepAttribution } from "@redotech/redo-billing-common/recover/recover-attribution-types";
import EditPencilIcon from "@redotech/redo-icons/edit-05.svg";
import DiscountIcon from "@redotech/redo-icons/percent-02_filled.svg";
import { useIsRedoAdmin } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { Treatment } from "@redotech/redo-merchant-marketing-rpc/schema/automation-outreach-splits/get-automation-outreach-split";
import { RecoverStep } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { DiscountSettings } from "@redotech/redo-model/discount/discount-db-parser";
import { formatDiscountDescription } from "@redotech/redo-model/discount/format-discount-description";
import {
  getEnabledScenarios,
  getModeLabel,
  getScenarioLabel,
  RecoverConfig,
} from "@redotech/redo-model/recover-config";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import {
  InspectorField,
  InspectorLayout,
  InspectorSection,
} from "@redotech/redo-web/inspector-v2";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PartialWithRequiredType, StepTypeDetailsProps } from ".";
import { AutomationAbTestEditor } from "./automation-ab-test-editor";
import { AutomationAbTestInspector } from "./automation-ab-test-inspector";
import { AutomationAbTestResultsPanel } from "./automation-ab-test-results-panel";
import { useAbTestEditor } from "./common/use-ab-test-editor";

type Props = StepTypeDetailsProps<PartialWithRequiredType<RecoverStep>>;

export function RecoverConfigPreview({
  step,
  setStep,
  save,
  automationId,
  schemaType,
}: Props) {
  const stepId = step.id;
  const recoverConfigId = step.templateId;
  const navigate = useNavigate();
  const location = useLocation();

  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);

  const [recoverConfigName, setRecoverConfigName] = useState("");
  const [creating, setCreating] = useState(false);

  const recoverConfigLoad = useLoad(async () => {
    if (!recoverConfigId) {
      return null;
    }
    try {
      return await marketingRpcClient.getRecoverConfigById({
        id: recoverConfigId,
      });
    } catch {
      return null;
    }
  }, [recoverConfigId, marketingRpcClient]);

  const recoverConfig = recoverConfigLoad.value;

  const [discountLoad, triggerDiscountLoad] = useTriggerLoad(async () => {
    const discountId = recoverConfig?.discountId;
    if (!discountId) {
      return null;
    }
    try {
      return await marketingRpcClient.getDiscount({ _id: discountId });
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (recoverConfig?.discountId) {
      triggerDiscountLoad();
    }
  }, [recoverConfig?.discountId, triggerDiscountLoad]);

  const discount = discountLoad.value;
  const discountLoading = recoverConfig?.discountId && discountLoad.pending;

  const handleCreateRecoverStep = useCallback(async () => {
    if (!recoverConfigName) {
      return;
    }
    setCreating(true);
    try {
      const newConfig = await marketingRpcClient.createRecoverConfig({
        name: recoverConfigName,
        schemaType,
      });
      setStep({ ...step, templateId: newConfig._id });
      save?.();
      navigate(
        `../../recover/builder/${newConfig._id}?returnPath=${encodeURIComponent(location.pathname)}&selected=${stepId}`,
      );
    } catch (error) {
      if (error instanceof Error) {
        toast(`Failed to create recover step: ${error.message}`, {
          variant: "error",
        });
      } else {
        toast("Failed to create recover step", { variant: "error" });
      }
    } finally {
      setCreating(false);
    }
  }, [
    recoverConfigName,
    setStep,
    step,
    save,
    marketingRpcClient,
    navigate,
    location.pathname,
    stepId,
    schemaType,
  ]);

  const createRecoverVariantTemplate = useCallback(
    async (name: string) => {
      const newConfig = await marketingRpcClient.createRecoverConfig({
        name,
        schemaType,
      });
      return newConfig._id;
    },
    [marketingRpcClient, schemaType],
  );

  const abTest = useAbTestEditor({
    automationId,
    stepId,
    stepType: "recover",
    splitId: step.splitId,
    existingTemplateId: recoverConfig?._id,
    existingTemplateName: recoverConfig?.name,
    onSplitIdChange: (splitId) => setStep({ ...step, splitId }),
    onSave: save,
    createTemplate: createRecoverVariantTemplate,
    getEditPath: (recoverConfigId, returnPath) =>
      `../../recover/builder/${recoverConfigId}?${returnPath}&stepType=${step.type}`,
    getViewPath: (recoverConfigId, returnPath) =>
      `../../recover/builder/${recoverConfigId}?${returnPath}&stepType=${step.type}&readonly=true`,
  });

  const buildReturnPath = useCallback(
    () =>
      `returnPath=${encodeURIComponent(location.pathname)}&selected=${stepId}&openAbTestEditor=true`,
    [location.pathname, stepId],
  );

  const handleEditVariant = useCallback(
    (treatment: Treatment) => {
      if (!treatment.templateId) {
        return;
      }
      save?.();
      navigate(
        `../../recover/builder/${treatment.templateId}?${buildReturnPath()}&stepType=${step.type}`,
      );
    },
    [navigate, buildReturnPath, save, step.type],
  );

  const handleViewVariant = useCallback(
    (treatment: Treatment) => {
      if (!treatment.templateId) {
        return;
      }
      navigate(
        `../../recover/builder/${treatment.templateId}?${buildReturnPath()}&stepType=${step.type}&readonly=true`,
      );
    },
    [navigate, buildReturnPath, step.type],
  );

  const handleOpenVariantModal = useCallback(() => {
    const variantLetter = String.fromCharCode(
      65 + (abTest.splitWithTreatments?.treatments.length ?? 1),
    );
    const variantName = `Variant ${variantLetter}`;
    abTest.variantModal.open(variantName, `${variantName} Recover Config`);
  }, [abTest.splitWithTreatments?.treatments.length, abTest.variantModal]);

  const handleEdit = () => {
    if (!recoverConfig) {
      return;
    }
    navigate(
      `../../recover/builder/${recoverConfig._id}?returnPath=${encodeURIComponent(location.pathname)}&selected=${stepId}`,
    );
  };

  const handleSelectWinner = (winningTemplateId: string) => {
    setStep({ ...step, templateId: winningTemplateId, splitId: undefined });
    save?.();
    abTest.refetch();
  };

  if (abTest.view === "loading" || recoverConfig === undefined) {
    return (
      <Flex align="center" grow={1} justify="center">
        <LoadingRedoAnimation />
      </Flex>
    );
  }

  if (abTest.view === "results" && automationId) {
    return (
      <AutomationAbTestResultsPanel
        automationId={automationId}
        automationStepId={stepId}
        onSelectWinner={handleSelectWinner}
        onViewVariant={handleViewVariant}
        stepType="recover"
        title="Recover A/B test"
      />
    );
  }

  if (abTest.view === "editor" && abTest.splitWithTreatments) {
    return (
      <>
        <AutomationAbTestEditor
          onAddVariant={handleOpenVariantModal}
          onClose={abTest.closeEditor}
          onEditVariant={handleEditVariant}
          renderVariantPreview={(treatment) => (
            <RecoverVariantPreview templateId={treatment.templateId} />
          )}
          splitWithTreatments={abTest.splitWithTreatments}
          stepType="recover"
          title="Recover A/B test"
        />

        <RedoModal
          isOpen={abTest.variantModal.isOpen}
          modalSize="md"
          onModalCloseRequested={abTest.variantModal.close}
          primaryButton={{
            text: "Continue",
            onClick: abTest.variantModal.create,
            disabled: !abTest.variantModal.templateName,
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: abTest.variantModal.close,
          }}
          title={`Create recover config for ${abTest.variantModal.variantName}`}
        >
          <Flex dir="column" gap="xs">
            <Text fontSize="xs" textColor="secondary">
              Configuration name
            </Text>
            <RedoTextInput
              placeholder="Enter configuration name"
              setValue={abTest.variantModal.setTemplateName}
              size="sm"
              value={abTest.variantModal.templateName}
            />
          </Flex>
        </RedoModal>
      </>
    );
  }

  return (
    <InspectorLayout title="Recover">
      <InspectorSection label="Messages">
        <Flex dir="column" gap="xs" w="full">
          <Flex align="center" justify="space-between" w="full">
            <Text fontSize="xs" textColor="secondary">
              Configuration
            </Text>
            {recoverConfig && (
              <RedoButton
                hierarchy="secondary"
                IconLeading={EditPencilIcon}
                onClick={handleEdit}
                size="xs"
                text="Edit"
              />
            )}
          </Flex>

          {recoverConfig ? (
            <RecoverConfigCard
              config={recoverConfig}
              discount={discount}
              discountLoading={!!discountLoading}
            />
          ) : (
            <Flex dir="column" gap="sm" w="full">
              <RedoTextInput
                placeholder="Enter recover step name"
                setValue={setRecoverConfigName}
                size="sm"
                value={recoverConfigName}
              />
              <RedoButton
                disabled={!recoverConfigName || creating}
                hierarchy="secondary"
                onClick={handleCreateRecoverStep}
                pending={creating}
                size="sm"
                text="Configure recover step"
              />
            </Flex>
          )}
        </Flex>
      </InspectorSection>

      <InspectorSection label="Step">
        <InspectorField label={null}>
          <RedoToggle
            label="Deactivate"
            setValue={(disabled) => setStep({ ...step, disabled })}
            size="sm"
            value={step.disabled ?? false}
          />
        </InspectorField>
      </InspectorSection>

      <AttributionSection setStep={setStep} step={step} />

      {automationId && (
        <AutomationAbTestInspector
          existingTest={abTest.splitWithTreatments}
          hasTemplate={!!recoverConfig?._id}
          onOpenEditor={abTest.openEditor}
        />
      )}
    </InspectorLayout>
  );
}

const RecoverConfigCard = memo(function RecoverConfigCard({
  config,
  discount,
  discountLoading,
}: {
  config: RecoverConfig;
  discount:
    | { name: string; discountSettings: DiscountSettings }
    | null
    | undefined;
  discountLoading: boolean;
}) {
  const enabledScenarios = getEnabledScenarios(config.scenarios);

  return (
    <Flex
      bgColor="primary"
      borderColor="primary"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      pb="md"
      radius="sm"
      w="full"
    >
      <Flex align="center" justify="space-between" pt="sm" px="md">
        <Text fontSize="sm" fontWeight="semibold">
          {config.name}
        </Text>
      </Flex>

      {(discountLoading || discount) && (
        <>
          <Divider />
          {discountLoading ? (
            <Flex align="center" justify="center" px="md" py="xs">
              <RedoSpinner size="xs" />
            </Flex>
          ) : discount ? (
            <Flex align="center" justify="space-between" px="md" py="xs">
              <Flex align="center" gap="xs">
                <DiscountIcon height={14} width={14} />
                <Text fontSize="xs" textColor="secondary">
                  {discount.name}
                </Text>
              </Flex>
              <Text fontSize="xs" fontWeight="medium" textColor="primary">
                {formatDiscountDescription(discount)}
              </Text>
            </Flex>
          ) : null}
        </>
      )}

      {enabledScenarios.length > 0 && (
        <>
          <Divider />
          <Flex px="md" py="xs">
            <Text fontSize="xxs" fontWeight="semibold" textColor="tertiary">
              SCENARIOS
            </Text>
          </Flex>
        </>
      )}

      {enabledScenarios.map((scenario, index) => (
        <Flex dir="column" key={index} w="full">
          {index > 0 && <Divider />}
          <Flex align="center" justify="space-between" px="md" py="xs">
            <Text fontSize="xs" textColor="secondary">
              {getScenarioLabel(scenario)}
            </Text>
            <Text
              fontSize="xs"
              overflow="hidden"
              style={{ maxWidth: "120px" }}
              textColor="secondary"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {getModeLabel(scenario)}
            </Text>
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
});

const RecoverVariantPreview = memo(function RecoverVariantPreview({
  templateId,
}: {
  templateId: string | undefined;
}) {
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);

  const configLoad = useLoad(async () => {
    if (!templateId) {
      return null;
    }
    try {
      return await marketingRpcClient.getRecoverConfigById({ id: templateId });
    } catch {
      return null;
    }
  }, [templateId, marketingRpcClient]);

  const config = configLoad.value;

  const discountLoad = useLoad(async () => {
    if (!config?.discountId) {
      return null;
    }
    try {
      return await marketingRpcClient.getDiscount({ _id: config.discountId });
    } catch {
      return null;
    }
  }, [config?.discountId, marketingRpcClient]);

  if (!templateId) {
    return (
      <Text fontSize="xs" textColor="tertiary">
        No template selected
      </Text>
    );
  }

  if (config === undefined) {
    return (
      <Flex align="center" justify="center" p="sm">
        <RedoSpinner size="xs" />
      </Flex>
    );
  }

  if (!config) {
    return (
      <Text fontSize="xs" textColor="tertiary">
        No recover configuration found
      </Text>
    );
  }

  return (
    <RecoverConfigCard
      config={config}
      discount={discountLoad.value}
      discountLoading={!!config.discountId && discountLoad.pending}
    />
  );
});

const DEFAULT_ATTRIBUTION: RecoverStepAttribution = {
  discountSettings: { enabled: true },
  clickSettings: { enabled: false },
  meaningfulConversationSettings: { enabled: false },
};

const AttributionSection = memo(function AttributionSection({
  step,
  setStep,
}: {
  step: PartialWithRequiredType<RecoverStep>;
  setStep: (step: PartialWithRequiredType<RecoverStep>) => void;
}) {
  const isRedoAdmin = useIsRedoAdmin();
  const hasOverride = step.attribution != null;
  const rawAttribution = step.attribution ?? DEFAULT_ATTRIBUTION;
  const attribution: RecoverStepAttribution = {
    discountSettings: {
      enabled:
        rawAttribution.discountSettings?.enabled ??
        DEFAULT_ATTRIBUTION.discountSettings.enabled,
    },
    clickSettings: {
      enabled:
        rawAttribution.clickSettings?.enabled ??
        DEFAULT_ATTRIBUTION.clickSettings.enabled,
    },
    meaningfulConversationSettings: {
      enabled:
        rawAttribution.meaningfulConversationSettings?.enabled ??
        DEFAULT_ATTRIBUTION.meaningfulConversationSettings.enabled,
    },
  };

  const updateAttribution = (updates: Partial<RecoverStepAttribution>) => {
    setStep({ ...step, attribution: { ...attribution, ...updates } });
  };

  const enabledMethods = [
    attribution.discountSettings.enabled && "discount",
    attribution.clickSettings.enabled && "click",
    attribution.meaningfulConversationSettings.enabled &&
      "meaningful conversation",
  ].filter((m): m is string => m !== false);

  if (!isRedoAdmin) {
    return (
      <InspectorSection label="Attribution">
        <Text fontSize="xs" textColor="secondary">
          {hasOverride
            ? enabledMethods.length > 0
              ? `Attributing via ${new Intl.ListFormat("en", { style: "long", type: "conjunction" }).format(enabledMethods)}`
              : "No attribution methods enabled"
            : "Using account defaults"}
        </Text>
      </InspectorSection>
    );
  }

  return (
    <InspectorSection label="Attribution">
      <Flex dir="column" gap="md">
        <Flex dir="column" gap="sm">
          <InspectorField label={null}>
            <RedoToggle
              disabled={!hasOverride}
              label="Discount attribution"
              setValue={(enabled) =>
                updateAttribution({ discountSettings: { enabled } })
              }
              size="sm"
              value={attribution.discountSettings.enabled}
            />
          </InspectorField>
          <InspectorField label={null}>
            <RedoToggle
              disabled={!hasOverride}
              label="Click attribution"
              setValue={(enabled) =>
                updateAttribution({
                  clickSettings: { ...attribution.clickSettings, enabled },
                })
              }
              size="sm"
              value={attribution.clickSettings.enabled}
            />
          </InspectorField>
          <InspectorField label={null}>
            <RedoToggle
              disabled={!hasOverride}
              label="Meaningful conversation"
              setValue={(enabled) =>
                updateAttribution({
                  meaningfulConversationSettings: {
                    ...attribution.meaningfulConversationSettings,
                    enabled,
                  },
                })
              }
              size="sm"
              value={attribution.meaningfulConversationSettings.enabled}
            />
          </InspectorField>
          <Flex pt="md">
            <RedoButton
              hierarchy="secondary"
              onClick={() => {
                if (hasOverride) {
                  const { attribution: _, ...rest } = step;
                  setStep(rest as PartialWithRequiredType<RecoverStep>);
                } else {
                  setStep({ ...step, attribution: DEFAULT_ATTRIBUTION });
                }
              }}
              size="xs"
              text={hasOverride ? "Reset to account defaults" : "Override"}
            />
          </Flex>
        </Flex>
      </Flex>
    </InspectorSection>
  );
});
